import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCallTranscript } from '../../api/telemetry'
import { cn } from '../../lib/utils'
import { MessageSquare, Bot, Wrench, Loader2, FileText, ChevronDown, ChevronUp } from 'lucide-react'
import { RecordingPlayer } from './RecordingPlayer'

function formatTimestamp(ts: number): string {
  return new Date(ts * 1000).toLocaleTimeString()
}

function detectRole(eventType: string): 'user' | 'bot' | 'tool' {
  if (eventType.includes('user')) return 'user'
  if (eventType.includes('bot')) return 'bot'
  if (eventType.includes('tool')) return 'tool'
  return 'bot'
}

const roleConfig = {
  user: { color: 'text-info', icon: MessageSquare },
  bot: { color: 'text-primary', icon: Bot },
  tool: { color: 'text-warning', icon: Wrench },
} as const

interface FullTranscriptProps {
  callSid: string
  recordingUrl?: string | null
  callSummary?: string | null
}

export function FullTranscript({ callSid, recordingUrl, callSummary }: FullTranscriptProps) {
  const { t } = useTranslation('calls')
  const { data, isLoading } = useCallTranscript(callSid)
  const [summaryExpanded, setSummaryExpanded] = useState(false)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-border bg-surface-elevated p-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="ml-2 text-text-secondary">{t('detail.transcript.loading')}</span>
      </div>
    )
  }

  const transcript = data?.transcript ?? []
  const summary = data?.summary || callSummary || ''
  const recording = data?.recording_url || recordingUrl || ''

  return (
    <div className="rounded-xl border border-border bg-surface-elevated p-6">
      <h2 className="mb-4 text-lg font-semibold text-text-primary">{t('detail.transcript.title')}</h2>

      {/* Recording Player */}
      {recording && (
        <div className="mb-4">
          <RecordingPlayer callSid={callSid} />
        </div>
      )}

      {/* Conversation messages */}
      {transcript.length === 0 ? (
        <p className="text-center text-sm text-text-muted py-8">{t('detail.transcript.empty')}</p>
      ) : (
        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
          {transcript.map((entry, i) => {
            const role = detectRole(entry.event_type)
            const config = roleConfig[role]
            const Icon = config.icon
            const roleLabel = t(`detail.transcript.role.${role}`)

            if (role === 'tool') {
              return (
                <div key={i} className="flex justify-center">
                  <div className="max-w-[85%] rounded-lg bg-surface/50 px-4 py-2 text-center">
                    <div className="mb-1 flex items-center justify-center gap-1">
                      <Icon className="h-3 w-3 text-warning" />
                      <span className="text-[10px] font-medium uppercase tracking-wider text-warning">
                        {roleLabel}
                      </span>
                      <span className="ml-2 text-[10px] text-text-muted">
                        {formatTimestamp(entry.timestamp)}
                      </span>
                    </div>
                    <p className="font-mono text-xs text-text-muted">{entry.text}</p>
                  </div>
                </div>
              )
            }

            const isUser = role === 'user'

            return (
              <div key={i} className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[75%] rounded-2xl px-4 py-2.5',
                    isUser
                      ? 'rounded-br-md bg-info/15 text-text-primary'
                      : 'rounded-bl-md bg-surface text-text-primary'
                  )}
                >
                  <div className={cn('mb-1 flex items-center gap-1', isUser ? 'justify-end' : 'justify-start')}>
                    <Icon className={cn('h-3 w-3', config.color)} />
                    <span className={cn('text-[10px] font-medium uppercase tracking-wider', config.color)}>
                      {roleLabel}
                    </span>
                    <span className="ml-2 text-[10px] text-text-muted">
                      {formatTimestamp(entry.timestamp)}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed">{entry.text}</p>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Call Summary (collapsible) */}
      {summary && (
        <div className="mt-4 border-t border-border pt-4">
          <button
            onClick={() => setSummaryExpanded(!summaryExpanded)}
            className="flex w-full items-center gap-2 text-left text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
          >
            <FileText className="h-4 w-4" />
            <span>{t('detail.transcript.summary')}</span>
            {summaryExpanded ? (
              <ChevronUp className="ml-auto h-4 w-4" />
            ) : (
              <ChevronDown className="ml-auto h-4 w-4" />
            )}
          </button>
          {summaryExpanded && (
            <p className="mt-2 text-sm leading-relaxed text-text-muted">
              {summary}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
